import { t } from './i18n.js';
import { useEffect, useState } from 'react';
import { api, send } from './api.js';
import { Modal, Notice, displayDate } from './components.js';
import { ConfirmIdentity, useConfirmationRequired } from './ConfirmIdentity.js';
export function MailAccess({ notify }: { notify: (text: string) => void }) {
  const [items, setItems] = useState<any[]>([]),
    [name, setName] = useState(''),
    [scope, setScope] = useState('both'),
    [days, setDays] = useState(30),
    [secret, setSecret] = useState(''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const confirm = useConfirmationRequired();
  const load = () =>
    api('/mail-access')
      .then(setItems)
      .catch((e) => setError(e.message));
  useEffect(() => {
    void load();
  }, []);
  return (
    <section className="settings-section">
      <h3>{t('Applications de messagerie')}</h3>
      <p className="muted">
        {t(
          'Un accès distinct pour chaque application, révocable à tout moment. Choisissez sa durée et ses droits.',
        )}
      </p>
      {error && <Notice type="error">{error}</Notice>}
      {confirm ? (
        <ConfirmIdentity returnTo="security" />
      ) : (
        <form
          className="stack-form"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setError('');
            try {
              const r = await send('/mail-access', {
                name,
                scopes: scope === 'both' ? ['imap', 'smtp'] : [scope],
                days,
              });
              setSecret(r.secret);
              setName('');
              await load();
            } catch (e: any) {
              setError(e.message);
            } finally {
              setBusy(false);
            }
          }}
        >
          <label>
            {t('Nom de l’application')}
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              minLength={2}
              maxLength={80}
              required
              placeholder={t('Mon ordinateur professionnel')}
            />
          </label>
          <label>
            {t('Droits')}
            <select value={scope} onChange={(e) => setScope(e.target.value)}>
              <option value="both">{t('Gestion des messages et envoi (IMAP et SMTP)')}</option>
              <option value="imap">{t('Gestion des messages (IMAP)')}</option>
              <option value="smtp">{t('Envoi (SMTP)')}</option>
            </select>
          </label>
          <label>
            {t('Durée')}
            <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
              <option value={7}>{t('7 jours')}</option>
              <option value={30}>{t('30 jours')}</option>
              <option value={90}>{t('90 jours')}</option>
              <option value={180}>{t('180 jours')}</option>
              <option value={360}>{t('360 jours')}</option>
            </select>
          </label>
          <button className="secondary" disabled={busy}>
            {t('Créer un accès d’application')}
          </button>
        </form>
      )}
      {items.map((item) => (
        <div className="session-row" key={item.id}>
          <div>
            <strong>{item.name}</strong>
            <small>
              {item.revokedAt
                ? t('Révoqué')
                : t('Expire le {date}', { date: displayDate(item.expiresAt) })}
            </small>
          </div>
          {!item.revokedAt && (
            <button
              className="text-button"
              onClick={async () => {
                if (
                  !window.confirm(
                    t('Révoquer {name} ? Les applications qui l’utilisent seront déconnectées.', {
                      name: item.name,
                    }),
                  )
                )
                  return;
                try {
                  await api(`/mail-access/${item.id}`, { method: 'DELETE' });
                  await load();
                  notify(t('Accès révoqué.'));
                } catch (e: any) {
                  setError(e.message);
                }
              }}
            >
              {t('Révoquer {name}', { name: item.name })}
            </button>
          )}
        </div>
      ))}
      {secret && (
        <Modal title={t('Conservez votre accès d’application')} onClose={() => setSecret('')}>
          <div className="stack-form">
            <Notice>
              {t(
                'Ce secret n’est affiché qu’une fois. Utilisez-le dans l’application de messagerie et conservez-le dans votre gestionnaire de mots de passe.',
              )}
            </Notice>
            <input readOnly value={secret} aria-label={t('Secret d’application')} />
            <button
              className="primary"
              onClick={() =>
                void navigator.clipboard
                  .writeText(secret)
                  .then(() => notify(t('Secret copié.')))
                  .catch(() =>
                    setError(t('Copie impossible : sélectionnez le secret pour le copier.')),
                  )
              }
            >
              {t('Copier le secret')}
            </button>
          </div>
        </Modal>
      )}
    </section>
  );
}
